import React, { useCallback, useRef } from 'react';
import { styled } from '@mui/material/styles';
import { Typography } from '@mui/material';
import MentionEditor from '../TextEditor/MentionEditor';
import { NODE_UI, stopPropagation } from './nodeUiStyles';

/**
 * NodeMentionEditor — TipTap-based text field with "$" placeholder trigger.
 * No toolbar, outputs plain text. For rich text with toolbar, use NodeRichTextEditor.
 *
 * Usage:
 *   <NodeMentionEditor
 *     label="Subject"
 *     value={subject}
 *     onChange={(val) => setSubject(val)}
 *     availablePlaceholders={placeholders}
 *   />
 */

const Label = styled(Typography)(({ theme }) => ({
  fontSize: NODE_UI.fontSize.label,
  fontWeight: NODE_UI.fontWeight.label,
  color: theme.palette.text.secondary,
  lineHeight: 1.4,
  marginBottom: '4px',
  userSelect: 'none',
}));

const EditorWrapper = styled('div')(({ theme }) => ({
  width: '100%',
  // Remove all extra borders — TipTap's own .tiptap-editor-container handles border/focus
  '& .tiptap-editor-container': {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: NODE_UI.borderRadiusSm,
    backgroundColor: theme.palette.background.default,
    boxShadow: 'none',
  },
  '& .tiptap-editor-container:hover': {
    borderColor: theme.palette.text.secondary,
  },
  '& .tiptap-editor-container:focus-within': {
    borderColor: theme.palette.primary.main,
    borderWidth: '1px',
    boxShadow: 'none',
  },
  // Override the bloated base TipTap styles
  '& .tiptap-editor-content': {
    padding: '0 !important',
    minHeight: 'auto !important',
  },
  '& .ProseMirror': {
    fontSize: NODE_UI.fontSize.value,
    minHeight: '32px',
    padding: '6px 8px',
    outline: 'none',
    border: 'none',
    '& p': { margin: 0 },
  },
  '&.node-mention-error .tiptap-editor-container': {
    borderColor: theme.palette.error.main,
  },
}));

const HelperText = styled(Typography)(({ theme }) => ({
  fontSize: NODE_UI.fontSize.caption,
  color: theme.palette.text.secondary,
  marginTop: '2px',
}));

const NodeMentionEditor = React.memo(function NodeMentionEditor({
  label,
  value = '',
  onChange,
  onFocus,
  availablePlaceholders = [],
  placeholder = 'Type $ to insert a placeholder',
  error = false,
  helperText,
  minHeight = '32px',
  variant = 'minimal',
  disabled = false,
  sx,
}) {
  const wrapperRef = useRef(null);

  const handleChange = useCallback((newValue) => {
    onChange?.(newValue);
  }, [onChange]);

  const handleFocus = useCallback(() => {
    onFocus?.();
  }, [onFocus]);

  return (
    <div
      onMouseDown={stopPropagation}
      onClick={stopPropagation}
      onPointerDown={stopPropagation}
      onWheel={stopPropagation}
    >
      {label && <Label>{label}</Label>}
      <EditorWrapper
        ref={wrapperRef}
        className={error ? 'node-mention-error' : ''}
        sx={sx}
      >
        <MentionEditor
          text={value}
          onChange={handleChange}
          onFocus={handleFocus}
          availablePlaceholders={availablePlaceholders}
          placeholder={placeholder}
          minHeight={minHeight}
          variant={variant}
          error={error}
          helperText={helperText}
        />
      </EditorWrapper>
      {helperText && (
        <HelperText color={error ? 'error' : 'textSecondary'}>
          {helperText}
        </HelperText>
      )}
    </div>
  );
});

export default NodeMentionEditor;
